import { toFinite } from '../math/MathUtils';

/**
 * Height of the virtual design space in world units.
 *
 * The camera always shows exactly this many units vertically, whatever the window is doing, so content
 * authored against it never needs a second layout pass.
 */
export const DESIGN_HEIGHT = 720;

/** Narrowest shape the world is allowed to fill; narrower windows letterbox top and bottom. */
export const MIN_ASPECT_RATIO = 4 / 3;

/** Widest shape the world is allowed to fill; wider windows pillarbox left and right. */
export const MAX_ASPECT_RATIO = 21 / 9;

/**
 * The area of the canvas the world is drawn into, and how much world it shows.
 *
 * Pixel figures are CSS pixels measured from the canvas' top left corner, matching what Pointer Events
 * report, so screen-to-world conversion needs no device pixel ratio. The renderer scales them by the
 * ratio when it sets up the backend viewport.
 */
export interface Viewport {
  /** World units visible horizontally. */
  readonly worldWidth: number;
  /** World units visible vertically; always {@link DESIGN_HEIGHT}. */
  readonly worldHeight: number;
  /** Aspect ratio actually used, after clamping the canvas' own ratio. */
  readonly aspectRatio: number;
  /** Distance in CSS pixels from the canvas left edge to the drawn area. */
  readonly offsetX: number;
  /** Distance in CSS pixels from the canvas top edge to the drawn area. */
  readonly offsetY: number;
  /** Width of the drawn area in CSS pixels. */
  readonly width: number;
  /** Height of the drawn area in CSS pixels. */
  readonly height: number;
}

/** Bounds a {@link computeViewport} call clamps the canvas aspect ratio into. */
export interface AspectRatioLimits {
  /** Narrowest permitted ratio. */
  readonly min: number;
  /** Widest permitted ratio. */
  readonly max: number;
}

const DEFAULT_LIMITS: AspectRatioLimits = { min: MIN_ASPECT_RATIO, max: MAX_ASPECT_RATIO };
const MIN_DRAWABLE_EXTENT = 1;

/**
 * Fits the world into a canvas of the given CSS size.
 *
 * Inside the aspect limits the world simply fills the canvas and a wider window reveals more world.
 * Outside them the ratio is clamped and the leftover is letterboxed, so the playfield can never become
 * an unusable slot and content can never drift off the edge of an extreme display.
 *
 * Pure, so the whole scaling policy is testable without a DOM.
 */
export function computeViewport(
  cssWidth: number,
  cssHeight: number,
  designHeight: number = DESIGN_HEIGHT,
  limits: AspectRatioLimits = DEFAULT_LIMITS,
): Viewport {
  const safeWidth = Math.max(MIN_DRAWABLE_EXTENT, toFinite(cssWidth));
  const safeHeight = Math.max(MIN_DRAWABLE_EXTENT, toFinite(cssHeight));
  const canvasRatio = safeWidth / safeHeight;
  const aspectRatio = Math.min(limits.max, Math.max(limits.min, canvasRatio));

  const isTooWide = canvasRatio > aspectRatio;
  const width = isTooWide ? safeHeight * aspectRatio : safeWidth;
  const height = isTooWide ? safeHeight : safeWidth / aspectRatio;

  return {
    worldWidth: designHeight * aspectRatio,
    worldHeight: designHeight,
    aspectRatio,
    offsetX: (safeWidth - width) / 2,
    offsetY: (safeHeight - height) / 2,
    width,
    height,
  };
}
